import type { SearchFacetTerm, SearchTermsFacet } from "@nosto/nosto-js/client"

/**
 * Detects if a terms facet should be rendered as color swatches.
 * A facet is treated as color when any of its values follows the
 * `Name|#hex` or `Name|http(s)://...` convention used by Bloomreach,
 * or when the facet name contains "color".
 */
export function isColorFacet(facet: SearchTermsFacet): boolean {
  const lowerName = facet.name?.toLowerCase() ?? ""
  if (lowerName.includes("color")) return true
  const sample = facet.data?.find(item => typeof item.value === "string")?.value
  if (!sample) return false
  return sample.includes("|#") || sample.includes("|http")
}

export type ParsedColorValue = {
  name: string
  /** Hex color or full image URL — empty string if none. */
  swatch: string
  isImage: boolean
}

/**
 * Humanizes a kebab-case facet value for display, replacing hyphens with
 * spaces so values like `"shirt-jackets"` render as `"shirt jackets"` in
 * checkbox labels and applied-filter pills. CSS (`text-transform`) handles
 * casing — this util only touches the separator.
 */
export function humanizeFacetValue(value: string): string {
  return value.replace(/-/g, " ")
}

/**
 * Parses a Bloomreach-style color value like "Black|#000000" or
 * "Camo|https://cdn.example.com/swatch.png". Returns the raw value as
 * the name when no swatch is provided.
 */
export function parseColorValue(raw: string): ParsedColorValue {
  const [name, swatch] = raw.split("|")
  const trimmedName = (name ?? raw).trim()
  const trimmedSwatch = (swatch ?? "").trim()
  return {
    name: trimmedName,
    swatch: trimmedSwatch,
    isImage: trimmedSwatch.startsWith("http")
  }
}

/**
 * Facet term enriched with the full list of original-case variants that were
 * merged into it. The frontend uses {@link MergedFacetTerm.variants} to apply
 * filters across every casing simultaneously so the rendered grid matches the
 * displayed count.
 */
export type MergedFacetTerm = SearchFacetTerm & {
  /** All original-case variants ("Loose", "loose", …) merged into this term. */
  variants: string[]
}

/**
 * Merges terms whose values only differ by case (e.g. "Loose" vs "loose").
 * The first occurrence wins for casing, counts are summed, the most permissive
 * selected flag wins so checked state is preserved, and every original case is
 * captured in `variants` so callers can apply filters across all of them at
 * once.
 */
export function dedupeFacetTerms(items: SearchFacetTerm[]): MergedFacetTerm[] {
  const byKey = new Map<string, MergedFacetTerm>()
  for (const item of items) {
    const key = (item.value ?? "").toLowerCase()
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, { ...item, variants: item.value ? [item.value] : [] })
      continue
    }
    existing.count = (existing.count ?? 0) + (item.count ?? 0)
    existing.selected = existing.selected || item.selected
    if (item.value && !existing.variants.includes(item.value)) {
      existing.variants.push(item.value)
    }
  }
  return Array.from(byKey.values())
}

/**
 * Secondary dedupe pass for color facets: merges terms whose parsed display
 * name matches even when the raw value encoding differs. Some products are
 * tagged with bare names (`"Black"`) while others use the swatch-bearing
 * form (`"Black|#000000"`); Nosto returns them as separate facet terms and
 * the case-dedupe in {@link dedupeFacetTerms} leaves them split (different
 * lowercase keys) — surfacing as two BLACK pills, one without a swatch dot.
 *
 * The representative term keeps the swatch-bearing raw value when one exists
 * (so the dot renders). `variants` accumulates every underlying raw value so
 * the filter still applies across all of them when the merged pill is
 * toggled.
 */
export function mergeColorFacetTerms(items: MergedFacetTerm[]): MergedFacetTerm[] {
  const byName = new Map<string, MergedFacetTerm>()
  for (const item of items) {
    const raw = String(item.value ?? "")
    const key = parseColorValue(raw).name.toLowerCase()
    if (!key) continue
    const existing = byName.get(key)
    if (!existing) {
      byName.set(key, { ...item, variants: [...(item.variants ?? [])] })
      continue
    }
    existing.count = (existing.count ?? 0) + (item.count ?? 0)
    existing.selected = existing.selected || item.selected
    for (const v of item.variants ?? []) {
      if (!existing.variants.includes(v)) existing.variants.push(v)
    }
    const existingHasSwatch = String(existing.value ?? "").includes("|")
    const incomingHasSwatch = raw.includes("|")
    if (incomingHasSwatch && !existingHasSwatch) {
      existing.value = item.value
    }
  }
  return Array.from(byName.values())
}
